/**
 * Finishd Home Dashboard
 * New homeowner experience with rich editorial UI
 */

import {
	Compass,
	DollarSign,
	FolderPlus,
	type LucideIcon,
	MessageCircle,
	TrendingUp,
	Upload,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { SkeletonRows } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	type ProposalDecision,
	ProposalDecisionDialog,
} from "@/features/projects/components/homeowner/proposals/proposal-decision-dialog";
import { rejectProposal } from "@/features/projects/services/projects-api.service";
import {
	DeskThreads,
	HeroHeader,
	HomeAtGlance,
	PhaseProgress,
	ProjectHero,
	RecentActivity,
	SearchBar,
	TeamSection,
} from "../components";
import { QuickActionCard } from "../components/dashboard-widgets";
import { DesignersNearYou } from "../components/designers-near-you";
import { HOME_SEARCH_SUGGESTIONS } from "../components/homeowner-hero";
import { NewUserHomePage } from "../components/new-user-home";
import {
	getHomeownerDashboard,
	type HomeownerDashboardData,
} from "../services/homeowner-dashboard-api.service";
import { capitalize, formatCountForUI } from "../utils/string-helpers";

const QUICK_ACTIONS = [
	{
		icon: FolderPlus,
		title: "Create project",
		subtitle: "Start your renovation journey",
	},
	{
		icon: Compass,
		title: "Browse inspiration",
		subtitle: "Rooms from real projects",
	},
	{
		icon: MessageCircle,
		title: "Talk to designer",
		subtitle: "",
	},
	{ icon: DollarSign, title: "Compare budget", subtitle: "Versus your BOQ" },
	{
		icon: Upload,
		title: "Upload a room",
		subtitle: "Coming soon",
	},
	{
		icon: TrendingUp,
		title: "AI room analysis",
		subtitle: "Coming soon",
	},
] as const;

// ============================================================================
// Dashboard Component
// ============================================================================

export function HomeDashboard() {
	const { user } = useAuthStore();
	const navigate = useNavigate();
	const [, setSearchParams] = useSearchParams();

	useEffect(() => {
		if (user?.userType && user.userType !== "homeowner") {
			navigate("/dashboard", { replace: true });
		}
	}, [user, navigate]);
	const [searchQuery, setSearchQuery] = useState("");
	const [dashboardData, setDashboardData] =
		useState<HomeownerDashboardData | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [showErrorDetails, setShowErrorDetails] = useState(false);
	const [decliningProposalId, setDecliningProposalId] = useState<string | null>(
		null,
	);
	const [declineDecision, setDeclineDecision] = useState<
		(ProposalDecision & { proposalId: string }) | null
	>(null);
	const toastShownRef = useRef(false);
	const declineControllerRef = useRef<AbortController | null>(null);
	const loadCleanupRef = useRef<(() => void) | null>(null);

	useEffect(() => {
		return () => {
			declineControllerRef.current?.abort();
		};
	}, []);

	useEffect(() => {
		if (user && user.userType !== "homeowner") {
			navigate("/dashboard", { replace: true });
		}
	}, [user, navigate]);

	const handleSearchChange = useCallback((query: string) => {
		setSearchQuery(query);
	}, []);

	const handleSearch = useCallback(
		(query: string) => {
			navigate(`/public-projects?search=${encodeURIComponent(query)}`);
		},
		[navigate],
	);

	const handleSuggestionClick = useCallback(
		(query: string) => {
			setSearchQuery(query);
			handleSearch(query);
		},
		[handleSearch],
	);

	const handleRetry = useCallback(() => {
		loadCleanupRef.current?.();

		let cancelled = false;
		const controller = new AbortController();

		const doRetry = async () => {
			try {
				setIsLoading(true);
				setError(null);
				setShowErrorDetails(false);
				toastShownRef.current = false;
				const data = await getHomeownerDashboard(controller.signal);
				if (!cancelled) {
					setDashboardData(data);
				}
			} catch (err) {
				if (cancelled) return;
				console.error("[HomeownerDashboard] Failed to load dashboard:", err);
				setError("Failed to load dashboard");
			} finally {
				if (!cancelled) {
					setIsLoading(false);
				}
			}
		};

		doRetry();

		const cleanup = () => {
			cancelled = true;
			controller.abort();
		};
		loadCleanupRef.current = cleanup;
		return cleanup;
	}, []);

	const handleDeclineProposal = useCallback(
		async (proposalId: string) => {
			declineControllerRef.current?.abort();
			const controller = new AbortController();
			declineControllerRef.current = controller;
			setDecliningProposalId(proposalId);
			try {
				const result = await rejectProposal(proposalId, {
					signal: controller.signal,
				});
				if (controller.signal.aborted) return;
				if (result.success) {
					toast.success("Proposal declined");
					handleRetry();
				} else {
					toast.error(result.error ?? "Failed to decline proposal");
				}
			} catch (_err) {
				if (controller.signal.aborted) return;
				toast.error("Failed to decline proposal");
			} finally {
				if (!controller.signal.aborted) {
					setDecliningProposalId(null);
				}
			}
		},
		[handleRetry],
	);

	const handleOpenProposal = useCallback(
		(projectId: string) => {
			navigate(`/projects/${projectId}?tab=team`);
		},
		[navigate],
	);

	const handleMessageDesigner = useCallback(
		(projectId: string) => {
			navigate(`/projects/${projectId}?tab=messages`);
		},
		[navigate],
	);

	const requestDecline = useCallback(
		(proposalId: string) => {
			const proposal = dashboardData?.deskThreads?.proposal;
			if (!proposal || proposal.proposalId !== proposalId) return;
			setDeclineDecision({
				mode: "reject",
				proposalId,
				designerName: proposal.designerName,
				costEstimate: proposal.costEstimate,
			});
		},
		[dashboardData],
	);

	const handleMessageUser = useCallback(
		(userId: string) => {
			const projectId = dashboardData?.project?.id;
			if (!projectId) return;
			setSearchParams({
				tab: "messages",
				messageThreadUserId: userId,
			});
			navigate(
				`/projects/${projectId}?tab=messages&messageThreadUserId=${userId}`,
			);
		},
		[dashboardData, navigate, setSearchParams],
	);

	useEffect(() => {
		handleRetry();
		return () => {
			loadCleanupRef.current?.();
		};
	}, [handleRetry]);

	const userName = dashboardData?.userName?.trim() || "Homeowner";
	const firstName = userName.split(" ")[0] ?? userName;
	const project = dashboardData?.project;
	const budget = dashboardData?.budget;
	const phases = dashboardData?.phases ?? [];
	const team = dashboardData?.team ?? [];
	const activities = dashboardData?.activities ?? [];
	const deskThreads = dashboardData?.deskThreads;
	const threadParts = dashboardData?.threadParts;
	const sectionErrors = dashboardData?.sectionErrors ?? {};

	useEffect(() => {
		if (error) return;

		if (
			dashboardData &&
			Object.keys(sectionErrors).length > 0 &&
			!toastShownRef.current
		) {
			setShowErrorDetails(false);
			toastShownRef.current = true;
			toast.custom(
				(t) => (
					<div className="flex flex-col gap-2 p-4 bg-dashboard-paper rounded-lg shadow-lg border border-dashboard-danger-border min-w-75">
						<div className="flex items-center justify-between">
							<span className="font-medium text-dashboard-pill-risk">
								Some sections failed to load
							</span>
							<button
								type="button"
								onClick={() => toast.dismiss(t)}
								className="text-dashboard-placeholder hover:text-dashboard-muted-dark cursor-pointer"
							>
								✕
							</button>
						</div>
						<button
							type="button"
							onClick={() => setShowErrorDetails((prev) => !prev)}
							className="text-sm text-dashboard-accent-dark underline text-left cursor-pointer"
						>
							Details {showErrorDetails ? "▲" : "▼"}
						</button>
						{showErrorDetails && (
							<ul className="text-sm text-dashboard-muted-dark pl-4 list-disc">
								{Object.entries(sectionErrors).map(([key, message]) => (
									<li key={key}>{message}</li>
								))}
							</ul>
						)}
						<button
							type="button"
							onClick={() => {
								toast.dismiss(t);
								handleRetry();
							}}
							className="text-sm font-medium text-dashboard-on-fill bg-dashboard-accent px-3 py-1.5 rounded-md hover:bg-dashboard-accent/90 cursor-pointer"
						>
							Refresh
						</button>
					</div>
				),
				{
					duration: 10000,
					onDismiss: () => {
						toastShownRef.current = false;
					},
				},
			);
		}
	}, [sectionErrors, dashboardData, handleRetry, showErrorDetails, error]);

	const defaultBudget = {
		released: 0,
		committed: 0,
		awaiting: 0,
		remaining: 0,
		percentage: 0,
	};

	if (isLoading) {
		return (
			<div
				className="min-h-screen flex items-center justify-center"
				style={{
					background:
						"radial-gradient(ellipse at 0% 0%, color-mix(in srgb, var(--color-dashboard-accent-light) 6%, transparent) 0%, transparent 45%), radial-gradient(ellipse at 100% 100%, color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent) 0%, transparent 50%), var(--color-dashboard-surface)",
				}}
			>
				<SkeletonRows
					label="Loading your dashboard..."
					rows={4}
					className="w-full max-w-3xl px-6"
					rowClassName="h-24 rounded-xl bg-dashboard-border-soft"
				/>
			</div>
		);
	}

	if (error) {
		return (
			<div
				className="min-h-screen flex items-center justify-center"
				style={{
					background:
						"radial-gradient(ellipse at 0% 0%, color-mix(in srgb, var(--color-dashboard-accent-light) 6%, transparent) 0%, transparent 45%), radial-gradient(ellipse at 100% 100%, color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent) 0%, transparent 50%), var(--color-dashboard-surface)",
				}}
			>
				<div className="text-center">
					<p className="font-display italic text-dashboard-accent-dark">
						{error ?? "No active project found"}
					</p>
					<p className="font-body text-sm text-dashboard-muted mt-2">
						Check back soon.
					</p>
				</div>
			</div>
		);
	}

	if (!project) {
		return <NewUserHomePage userName={firstName} />;
	}

	return (
		<div
			className="min-h-screen"
			style={{
				background:
					"radial-gradient(ellipse at 0% 0%, color-mix(in srgb, var(--color-dashboard-accent-light) 6%, transparent) 0%, transparent 45%), radial-gradient(ellipse at 100% 100%, color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent) 0%, transparent 50%), var(--color-dashboard-surface)",
			}}
		>
			{/* Hero Section */}
			<div className="dashboard-container">
				<HeroHeader userName={firstName} threadParts={threadParts} />
			</div>

			{/* Search across public projects */}
			<SearchBar
				value={searchQuery}
				onChange={handleSearchChange}
				suggestions={HOME_SEARCH_SUGGESTIONS}
				onSuggestionClick={handleSuggestionClick}
				onSearch={handleSearch}
			/>

			{/* Active Project Hero */}
			<div className="dashboard-container">
				<ProjectHero
					projectId={project.id}
					projectName={project.name}
					location={project.location}
					propertyType={project.propertyType}
					area={project.area}
					nextMilestone={project.nextMilestone}
					milestoneAmount={project.milestoneAmount}
					stageNumber={project.stageNumber}
					totalStages={project.totalStages}
					segments={project.segments}
					imageUrl={project.imageUrl}
					daysInProject={project.daysIn}
					recentComment={project.recentComment}
				/>
			</div>

			{/* Quick Actions */}
			<div className="dashboard-container pt-8">
				<div className="grid grid-cols-2 gap-4.5 md:grid-cols-6">
					{QUICK_ACTIONS.map((action) => (
						<QuickActionCard
							key={action.title}
							icon={action.icon as LucideIcon}
							title={action.title}
							subtitle={action.subtitle}
							onClick={
								action.title === "Create project"
									? () => navigate("/projects/new")
									: action.title === "Browse inspiration"
										? () => navigate("/inspire")
										: action.title === "Talk to designer" ||
												action.title === "Compare budget"
											? () => {
													const projectId = dashboardData?.project?.id;
													if (!projectId) {
														navigate("/projects/new");
														return;
													}
													const tab =
														action.title === "Talk to designer"
															? "messages"
															: "boq";
													navigate(`/projects/${projectId}?tab=${tab}`);
												}
											: undefined
							}
						/>
					))}
				</div>
			</div>

			{/* Home at a Glance */}
			<HomeAtGlance
				project={project}
				projectId={project.id}
				budget={budget ?? defaultBudget}
			/>

			{/* Phase Progress Section */}
			<div className="dashboard-container pt-12">
				<PhaseProgress
					phases={phases}
					title={
						<>
							{capitalize(formatCountForUI(phases.length))}{" "}
							<span className="italic text-dashboard-accent-dark">
								chapters
							</span>
							, {formatCountForUI(project.daysIn)} days in.
						</>
					}
				/>
			</div>

			{/* Desk Threads — only show when there's an active submitted proposal */}
			{deskThreads?.proposal?.proposalId && (
				<div className="pt-20">
					<DeskThreads
						proposal={deskThreads.proposal}
						designer={deskThreads.designer}
						onDecline={requestDecline}
						onOpenProposal={handleOpenProposal}
						onMessageDesigner={handleMessageDesigner}
						isDeclining={decliningProposalId !== null}
					/>
					<ProposalDecisionDialog
						decision={declineDecision}
						onConfirm={() => {
							if (declineDecision) {
								void handleDeclineProposal(declineDecision.proposalId);
							}
						}}
						onCancel={() => setDeclineDecision(null)}
					/>
				</div>
			)}

			{/* Recent Activity + Team */}
			<div className="dashboard-container pt-20">
				<div className="grid grid-cols-1 gap-5.5 md:grid-cols-[1.3fr_1fr]">
					<RecentActivity
						activities={activities}
						title={
							<>
								The week,{" "}
								<span className="italic text-dashboard-accent-dark">
									noted.
								</span>
							</>
						}
						entryCount={activities.length}
					/>
					<TeamSection
						members={team}
						title={
							<>
								People{" "}
								<span className="italic text-dashboard-accent-dark">
									on-site.
								</span>
							</>
						}
						memberCount={team.length}
						onMessageUser={handleMessageUser}
					/>
				</div>
			</div>

			{/* Verified designers, from the marketplace directory */}
			<DesignersNearYou />

			{/* Footer */}
			<div
				className="dashboard-container"
				style={{ padding: "60px 0 80px", textAlign: "center" }}
			>
				<span className="font-display italic font-semibold text-[16px] text-dashboard-muted">
					Because starting is common. Finishing well is not.
				</span>
			</div>
		</div>
	);
}
